import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useMemo } from 'react';

import {
  ScatterChart,
  ScatterChartProps,
} from '~/pages/Company/components/Inference/components/ScatterChart';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';
import { ceilToStep } from '~/utils/helpers';

import styles from './styles.module.scss';

interface Props {
  pcaX: number;
  pcaY: number;
  uzXTh: number;
  data: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['unicorn_zone'];
  className?: string;
}

const Component: React.FC<Props> = ({ pcaX, pcaY, uzXTh, data, className }) => {
  const xAxisDomain = useMemo(() => {
    return [Math.min(-4, ceilToStep(pcaX - 2, 2)), Math.max(10, ceilToStep(pcaX + 2, 2))];
  }, [pcaX]);

  const yAxisDomain = useMemo(() => {
    return [Math.min(-8, ceilToStep(pcaY - 2, 2)), Math.max(4, ceilToStep(pcaY + 2, 2))];
  }, [pcaY]);

  const areas = useMemo<ScatterChartProps['areas']>(() => {
    return [
      {
        x1: uzXTh,
        y1: yAxisDomain[0],
        x2: xAxisDomain[1],
        y2: yAxisDomain[1],
        type: 'success',
      },
    ];
  }, [uzXTh, xAxisDomain, yAxisDomain]);

  const scatterChartData = useMemo<ScatterChartProps['data']>(() => {
    return data.map(data => ({ x: data.x, y: data.y }));
  }, [data]);

  const redDot = useMemo<ScatterChartProps['redDot']>(() => {
    return { x: pcaX, y: pcaY };
  }, [pcaX, pcaY]);

  return (
    <div className={clsx(styles.container, className)}>
      <Typography color="var(--text-3-color)" mb="27px" variant="subtitle1">
        Unicorn Zone
      </Typography>
      <ScatterChart
        areas={areas}
        data={scatterChartData}
        redDot={redDot}
        xAxisDomain={xAxisDomain}
        xAxisLabel="x"
        xAxisTickCount={8}
        yAxisDomain={yAxisDomain}
        yAxisLabel="y"
        yAxisTickCount={7}
      />
    </div>
  );
};

export const UnicornZoneScatter = memo(Component);
